import { CHECK_STATUSES } from '../constants/appConstants';
import { formatCurrency, formatCurrencyForPdf, formatCurrencyWithCode, getCurrencySymbol } from './currency';
import { formatDateTime } from './dateFormat';
import { sortOrderItemsByCreatedAt, getItemExportTypeLabel } from './orderItemDisplay';

export function getCheckStatusColor(status) {
  switch (status) {
    case CHECK_STATUSES.OPEN: return 'info';
    case CHECK_STATUSES.PARTIALLY_PAID: return 'warning';
    case CHECK_STATUSES.CLOSED: return 'success';
    default: return 'default';
  }
}

/** Format list-row totals (always in check currency from API). */
export function formatCheckListAmount(amount, checkOrCurrency) {
  const currency = typeof checkOrCurrency === 'string'
    ? checkOrCurrency
    : checkOrCurrency?.checkCurrency;
  return formatCurrency(amount, currency);
}

export function getCheckStatusLabel(status, t) {
  const labels = {
    [CHECK_STATUSES.OPEN]: t('status.open'),
    [CHECK_STATUSES.PARTIALLY_PAID]: t('status.partiallyPaid'),
    [CHECK_STATUSES.CLOSED]: t('status.closed'),
  };
  return labels[status] || status || t('common.emptyValue');
}

export function checkItemsNeedConversionColumn(items, checkCurrency) {
  if (!checkCurrency || !items?.length) return false;
  return items.some((item) => item?.currency && item.currency !== checkCurrency);
}

/** Whether the check page should show exchange rates (edit or read-only). */
export function checkShowsExchangeRates(items, balance, check) {
  if (!balance?.checkCurrency) return false;
  const stored = check?.exchangeRates?.length > 0 || balance?.exchangeRates?.length > 0;
  if (stored) return true;
  if (balance.showDualCurrency) return true;
  if (balance.defaultItemCurrency && balance.defaultItemCurrency !== balance.checkCurrency) {
    return true;
  }
  return checkItemsNeedConversionColumn(items, balance.checkCurrency);
}

export function formatExchangeRateLine(fromCurrency, rate, toCurrency, t) {
  const formattedRate = parseFloat(Number(rate).toFixed(4));
  return t('export.exchangeRateLine', {
    from: fromCurrency,
    rate: formattedRate,
    to: getCurrencySymbol(toCurrency),
  });
}

export function formatCheckExchangeRateLines(exchangeRates, checkCurrency, t) {
  if (!checkCurrency || !exchangeRates?.length) return [];
  return exchangeRates
    .filter((row) => row?.toCurrency === checkCurrency && row?.fromCurrency && row?.rate > 0)
    .map((row) => formatExchangeRateLine(row.fromCurrency, row.rate, row.toCurrency, t));
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function itemPricesIncludeVat(item, balance) {
  if (typeof item?.pricesIncludeVat === 'boolean') return item.pricesIncludeVat;
  return balance?.pricesIncludeVat === true;
}

/** PDF lines: treat missing item flag as VAT-inclusive when check has VAT. */
function itemPricesIncludeVatForPdf(item, balance) {
  if (item?.pricesIncludeVat === false) return false;
  if (item?.pricesIncludeVat === true) return true;
  const vatPct = Number(balance?.vatPercentage) || 0;
  return vatPct > 0 || balance?.pricesIncludeVat === true;
}

/** Gross line amount → ex-VAT when the item price includes tax. */
export function amountExcludingVat(amount, item, balance, { forPdf = false } = {}) {
  const value = Number(amount) || 0;
  const vatPct = Number(balance?.vatPercentage) || 0;
  const includesVat = forPdf
    ? itemPricesIncludeVatForPdf(item, balance)
    : itemPricesIncludeVat(item, balance);
  if (vatPct <= 0 || !includesVat) {
    return roundMoney(value);
  }
  return roundMoney((value * 100) / (100 + vatPct));
}

/** Rate: 1 unit of defaultItemCurrency → checkCurrency (from balance or exchangeRates). */
function getDefaultToCheckRate(balance) {
  const sub = balance?.subtotal;
  const subDef = balance?.subtotalInDefaultCurrency;
  if (sub > 0 && subDef > 0) return sub / subDef;

  const from = balance?.defaultItemCurrency;
  const to = balance?.checkCurrency;
  const row = (balance?.exchangeRates || []).find(
    (r) => r.fromCurrency === from && r.toCurrency === to,
  );
  if (row?.rate > 0) return row.rate;
  return null;
}

function resolveCheckLineAmount(item, balance) {
  const checkCurrency = balance?.checkCurrency;
  const defaultCurrency = balance?.defaultItemCurrency || checkCurrency;
  const itemCurrency = item?.currency || defaultCurrency;
  const original = roundMoney(item?.finalPrice || 0);

  const line = balance?.lines?.find((l) => String(l.itemId) === String(item._id));

  let checkAmount = line?.convertedAmount;
  if (checkAmount == null || !Number.isFinite(checkAmount)) {
    if (itemCurrency === checkCurrency) {
      checkAmount = original;
    } else if (Number.isFinite(item?.fxRateToCheckCurrency) && item.fxRateToCheckCurrency > 0) {
      checkAmount = roundMoney(original * item.fxRateToCheckCurrency);
    }
  } else {
    checkAmount = roundMoney(checkAmount);
  }

  let defaultAmount = original;
  if (itemCurrency !== defaultCurrency) {
    if (itemCurrency === checkCurrency && checkAmount != null) {
      const rate = getDefaultToCheckRate(balance);
      if (rate > 0) defaultAmount = roundMoney(checkAmount / rate);
    } else if (checkAmount != null && defaultCurrency !== checkCurrency) {
      const rate = getDefaultToCheckRate(balance);
      if (rate > 0) defaultAmount = roundMoney(checkAmount / rate);
    }
  }

  return { defaultAmount, checkAmount, defaultCurrency, checkCurrency, itemCurrency };
}

/** Line-item sum excluding VAT (prefer server-computed fields when present). */
export function getSubtotalBeforeVat(balance) {
  if (!balance) {
    return { check: 0, default: 0 };
  }
  if (balance.subtotalBeforeVat != null) {
    return {
      check: roundMoney(balance.subtotalBeforeVat),
      default: roundMoney(
        balance.subtotalBeforeVatInDefaultCurrency ?? balance.subtotalBeforeVat,
      ),
    };
  }
  const check = roundMoney(balance.subtotal);
  const def = roundMoney(balance.subtotalInDefaultCurrency);
  if (balance.pricesIncludeVat && balance.vatPercentage > 0) {
    return {
      check: roundMoney(check - (balance.vatAmount || 0)),
      default: roundMoney(def - (balance.vatAmountInDefaultCurrency || 0)),
    };
  }
  return { check, default: def };
}

/** Pre-discount subtotal before VAT (adds check discount back onto post-discount subtotal). */
export function getSubtotalBeforeVatBeforeDiscount(balance) {
  const current = getSubtotalBeforeVat(balance);
  const discountCheck = Number(balance?.discountValue) || 0;
  if (discountCheck <= 0) return current;
  const discountDefault = Number(
    balance?.discountValueInDefaultCurrency ?? balance?.discountValue,
  ) || 0;
  return {
    check: roundMoney(current.check + discountCheck),
    default: roundMoney(current.default + discountDefault),
  };
}

/** VAT on the pre-discount subtotal (always from exclusive base × rate). */
export function getVatBeforeDiscount(balance) {
  const preSub = getSubtotalBeforeVatBeforeDiscount(balance);
  const vatPct = Number(balance?.vatPercentage) || 0;
  if (vatPct <= 0) {
    return { check: 0, default: 0 };
  }
  return {
    check: roundMoney(preSub.check * vatPct / 100),
    default: roundMoney(preSub.default * vatPct / 100),
  };
}

/** Gross total before discount (pre-discount subtotal + VAT on that base). */
export function getGrossTotalBeforeDiscount(balance) {
  const netCheck = roundMoney(balance?.total ?? balance?.totalItems ?? 0);
  const netDefault = roundMoney(balance?.totalInDefaultCurrency ?? netCheck);
  if (!(Number(balance?.discountValue) > 0)) {
    return { check: netCheck, default: netDefault };
  }
  const preSub = getSubtotalBeforeVatBeforeDiscount(balance);
  const preVat = getVatBeforeDiscount(balance);
  return {
    check: roundMoney(preSub.check + preVat.check),
    default: roundMoney(preSub.default + preVat.default),
  };
}

/** Discount impact on the gross total (includes VAT effect), for display after Total. */
export function getGrossDiscount(balance) {
  const discountCheck = Number(balance?.discountValue) || 0;
  if (discountCheck <= 0) {
    return { check: 0, default: 0 };
  }
  const gross = getGrossTotalBeforeDiscount(balance);
  const netCheck = roundMoney(balance?.total ?? balance?.totalItems ?? 0);
  const netDefault = roundMoney(balance?.totalInDefaultCurrency ?? netCheck);
  return {
    check: roundMoney(gross.check - netCheck),
    default: roundMoney(gross.default - netDefault),
  };
}

function paymentAmountsForSummary(tx, balance) {
  const checkCur = balance?.checkCurrency;
  const defCur = balance?.defaultItemCurrency || checkCur;
  const amountCheck = roundMoney(
    tx.amountInCheckCurrency != null ? tx.amountInCheckCurrency : tx.amount,
  );
  if (!balance?.showDualCurrency || !defCur || defCur === checkCur) {
    return { amountDefault: amountCheck, amountCheck };
  }
  if (tx.currency === defCur) {
    return { amountDefault: roundMoney(tx.amount), amountCheck };
  }
  const rate = getDefaultToCheckRate(balance);
  if (rate > 0) {
    return { amountDefault: roundMoney(amountCheck / rate), amountCheck };
  }
  return { amountDefault: roundMoney(tx.amount), amountCheck };
}

function formatAmountPair(amountDefault, amountCheck, balance, forPdf) {
  const fmt = forPdf ? formatCurrencyForPdf : formatCurrency;
  const dual = balance?.showDualCurrency
    && balance.defaultItemCurrency
    && balance.checkCurrency
    && balance.defaultItemCurrency !== balance.checkCurrency;

  if (!dual) {
    return fmt(amountCheck ?? amountDefault, balance?.checkCurrency || balance?.defaultItemCurrency);
  }
  return {
    default: fmt(amountDefault, balance.defaultItemCurrency),
    check: fmt(amountCheck, balance.checkCurrency),
  };
}

export function buildCheckBalanceSummaryRows(balance, t, { forPdf = false, transactions = [] } = {}) {
  if (!balance) return [];
  const dual = balance.showDualCurrency && balance.defaultItemCurrency !== balance.checkCurrency;

  const row = (label, amountDefault, amountCheck) => {
    const formatted = formatAmountPair(amountDefault, amountCheck, balance, forPdf);
    if (!dual || typeof formatted === 'string') {
      return [label, formatted];
    }
    if (forPdf) {
      return [label, formatted.default, formatted.check];
    }
    return [label, `${formatted.check}\n${formatted.default}`];
  };

  const subtotalBeforeVat = getSubtotalBeforeVatBeforeDiscount(balance);

  const rows = [
    row(
      t('checks.subtotalBeforeVat'),
      subtotalBeforeVat.default,
      subtotalBeforeVat.check,
    ),
  ];

  if (balance.vatPercentage > 0) {
    const vatBeforeDiscount = Number(balance.discountValue) > 0
      ? getVatBeforeDiscount(balance)
      : {
        check: roundMoney(balance.vatAmount),
        default: roundMoney(balance.vatAmountInDefaultCurrency ?? balance.vatAmount),
      };
    rows.push(row(
      t('checks.vat', { percent: balance.vatPercentage }),
      vatBeforeDiscount.default,
      vatBeforeDiscount.check,
    ));
  }

  const grossTotal = getGrossTotalBeforeDiscount(balance);
  rows.push(
    row(t('checks.total'), grossTotal.default, grossTotal.check),
  );

  if (Number(balance.discountValue) > 0) {
    const discountLabel = balance.discountType === 'percent'
      ? t('checks.discountPercent', { percent: balance.discountPercent })
      : t('checks.discountFixed');
    const grossDiscount = getGrossDiscount(balance);
    rows.push(row(discountLabel, -grossDiscount.default, -grossDiscount.check));
  }

  const paymentTxs = (transactions || []).filter(
    (tx) => !tx.cancelled && (tx.type === 'payment' || tx.type === 'deposit'),
  );
  if (paymentTxs.length > 0) {
    paymentTxs.forEach((tx) => {
      const { amountDefault, amountCheck } = paymentAmountsForSummary(tx, balance);
      const dateSuffix = tx.createdAt ? ` (${formatDateTime(tx.createdAt)})` : '';
      rows.push(row(
        `${t('checks.payment')}${dateSuffix}`,
        amountDefault,
        amountCheck,
      ));
    });
  } else {
    rows.push(
      row(t('cashiering.payments'), balance.totalDepositsInDefaultCurrency, balance.totalDeposits),
    );
  }

  rows.push(
    row(t('cashiering.amountDue'), balance.amountDueInDefaultCurrency, balance.amountDue),
  );

  return rows;
}

function formatPdfExchangeRateValue(rate) {
  const n = Number(rate);
  if (!Number.isFinite(n) || n <= 0) return null;
  return parseFloat(n.toFixed(4)).toString();
}

/**
 * Exchange rates used on this check (from → check currency), for PDF footer.
 */
export function buildCheckPdfExchangeRateLines(items, balance, t, check) {
  const checkCurrency = balance?.checkCurrency || check?.checkCurrency;
  if (!checkCurrency) return [];

  const rateMap = new Map();

  const addRate = (from, rate) => {
    const formatted = formatPdfExchangeRateValue(rate);
    if (!from || from === checkCurrency || !formatted) return;
    if (!rateMap.has(from)) rateMap.set(from, formatted);
  };

  for (const row of check?.exchangeRates || balance?.exchangeRates || []) {
    if (row?.toCurrency === checkCurrency) {
      addRate(row.fromCurrency, row.rate);
    }
  }

  for (const line of balance?.lines || []) {
    addRate(line.sourceCurrency, line.fxRate);
  }

  for (const item of items || []) {
    addRate(item?.currency, item?.fxRateToCheckCurrency);
  }

  const defaultCurrency = balance?.defaultItemCurrency;
  if (defaultCurrency && defaultCurrency !== checkCurrency && !rateMap.has(defaultCurrency)) {
    const subDef = balance.subtotalInDefaultCurrency;
    const subCheck = balance.subtotal;
    if (subDef > 0 && subCheck > 0) {
      addRate(defaultCurrency, subCheck / subDef);
    }
  }

  return [...rateMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([from, rate]) => formatExchangeRateLine(from, rate, checkCurrency, t));
}

export function formatItemPriceWithConversion(item, balance) {
  const checkCurrency = balance?.checkCurrency;
  if (!checkCurrency) {
    return formatCurrency(item.finalPrice, item.currency);
  }

  if (balance?.lines?.length) {
    const {
      defaultAmount,
      checkAmount,
      defaultCurrency,
      checkCurrency: chkCur,
      itemCurrency,
    } = resolveCheckLineAmount(item, balance);

    const showDual = balance.showDualCurrency
      && balance.defaultItemCurrency
      && chkCur
      && balance.defaultItemCurrency !== chkCur;

    if (showDual && itemCurrency !== chkCur && checkAmount != null) {
      return `${formatCurrency(defaultAmount ?? item.finalPrice, defaultCurrency)} (${formatCurrencyWithCode(checkAmount, chkCur)})`;
    }
    if (checkAmount != null) {
      return formatCurrency(checkAmount, chkCur);
    }
  }

  if (!item?.currency || item.currency === checkCurrency) {
    return formatCurrency(item.finalPrice, item.currency || checkCurrency);
  }
  const converted = item.fxRateToCheckCurrency
    ? (item.finalPrice || 0) * item.fxRateToCheckCurrency
    : null;
  if (converted == null) {
    return `${formatCurrency(item.finalPrice, item.currency)} → ${checkCurrency}`;
  }
  return `${formatCurrency(item.finalPrice, item.currency)} (${formatCurrencyWithCode(converted, checkCurrency)})`;
}

export function formatItemPriceForPdf(item, balance) {
  const checkCurrency = balance?.checkCurrency;
  const defaultCurrency = balance?.defaultItemCurrency || checkCurrency;
  const showDual = balance?.showDualCurrency && defaultCurrency && checkCurrency
    && defaultCurrency !== checkCurrency;

  if (!showDual) {
    const itemCurrency = item?.currency || checkCurrency;
    const exVat = amountExcludingVat(item?.finalPrice, item, balance, { forPdf: true });
    return {
      itemPrice: formatCurrencyForPdf(exVat, itemCurrency),
      checkPrice: null,
      needsConversion: false,
    };
  }

  const { defaultAmount, checkAmount, defaultCurrency: defCur, checkCurrency: chkCur } =
    resolveCheckLineAmount(item, balance);

  return {
    itemPrice: formatCurrencyForPdf(
      amountExcludingVat(defaultAmount, item, balance, { forPdf: true }),
      defCur,
    ),
    checkPrice: checkAmount != null
      ? formatCurrencyForPdf(amountExcludingVat(checkAmount, item, balance, { forPdf: true }), chkCur)
      : null,
    needsConversion: true,
  };
}

function formatPdfPricePerUnit(item, balance, getItemCarat) {
  const carat = getItemCarat(item);
  if (carat == null || carat <= 0) return null;
  let ppu = item.pricePerUnit ?? (item.finalPrice != null ? item.finalPrice / carat : null);
  if (ppu == null || !Number.isFinite(ppu)) return null;
  ppu = amountExcludingVat(ppu, item, balance, { forPdf: true });
  const currency = item?.currency || balance?.checkCurrency;
  const symbol = getCurrencySymbol(currency);
  const value = Number(ppu).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${symbol}${value}`;
}

export function buildCheckPdfExportTable(items, balance, t, helpers) {
  const {
    getOrderItemDescriptionCell,
    getItemDetails,
    getItemPricePerCt,
    isCustomByWeightItem,
    weightUnitShort,
    emptyDash,
  } = helpers;

  const needConversion = Boolean(
    balance?.showDualCurrency
    && balance?.defaultItemCurrency
    && balance?.checkCurrency
    && balance.defaultItemCurrency !== balance.checkCurrency,
  );

  const pdfExVat = (balance?.vatPercentage || 0) > 0;
  const columns = [
    t('orderItems.columns.date'),
    t('orderItems.columns.certNumber'),
    t('orderItems.columns.description'),
    t('orderItems.columns.weight'),
    t('cashiering.pricePerCt'),
    needConversion
      ? t(pdfExVat ? 'export.priceItemExVat' : 'export.priceItem', {
        currency: getCurrencySymbol(balance?.defaultItemCurrency),
      })
      : t(pdfExVat ? 'export.priceExVat' : 'cashiering.price'),
  ];
  if (needConversion) {
    columns.push(t(pdfExVat ? 'export.priceCheckExVat' : 'export.priceCheck', {
      currency: getCurrencySymbol(balance?.checkCurrency),
    }));
  }

  const sortedItems = sortOrderItemsByCreatedAt(items);
  const rows = sortedItems.map((item) => {
    const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
    const pricePerCt = formatPdfPricePerUnit(item, balance, helpers.getItemCarat)
      ?? getItemPricePerCt(item);
    const pdfPrices = formatItemPriceForPdf(item, balance);
    const row = [
      formatDateTime(item.createdAt) || emptyDash,
      getItemExportTypeLabel(item, t),
      getOrderItemDescriptionCell(item),
      getItemDetails(item),
      pricePerCt === emptyDash ? emptyDash : `${pricePerCt}/${weightSuffix}`,
      pdfPrices.itemPrice,
    ];
    if (needConversion) {
      row.push(pdfPrices.checkPrice ?? emptyDash);
    }
    return row;
  });

  const totalWeight = sortedItems.reduce((sum, item) => sum + (helpers.getItemCarat(item) || 0), 0);
  const currency = balance?.checkCurrency;
  const subtotalBeforeVat = getSubtotalBeforeVatBeforeDiscount(balance);
  const totalRow = [
    '',
    '',
    t('export.totalLabel'),
    t('export.totalWeightCt', { weight: totalWeight.toFixed(2) }),
    '',
    formatCurrencyForPdf(
      subtotalBeforeVat.default,
      balance?.showDualCurrency ? balance.defaultItemCurrency : currency,
    ),
  ];
  if (needConversion) {
    totalRow.push(formatCurrencyForPdf(subtotalBeforeVat.check, currency));
  }

  return { columns, rows, totalRow, needConversion };
}

function formatGrossItemPriceForPdf(item, balance, emptyDash) {
  const checkCurrency = balance?.checkCurrency;
  const defaultCurrency = balance?.defaultItemCurrency || checkCurrency;
  const showDual = balance?.showDualCurrency && defaultCurrency && checkCurrency
    && defaultCurrency !== checkCurrency;

  if (!showDual) {
    const itemCurrency = item?.currency || checkCurrency;
    return {
      itemPrice: formatCurrencyForPdf(item?.finalPrice ?? 0, itemCurrency),
      checkPrice: null,
    };
  }

  const { defaultAmount, checkAmount, defaultCurrency: defCur, checkCurrency: chkCur } =
    resolveCheckLineAmount(item, balance);

  return {
    itemPrice: formatCurrencyForPdf(defaultAmount ?? item?.finalPrice ?? 0, defCur),
    checkPrice: checkAmount != null
      ? formatCurrencyForPdf(checkAmount, chkCur)
      : emptyDash,
  };
}

/**
 * Order-style check items PDF: gross line prices, optional dual-currency columns, no VAT or balance summary.
 */
export function buildCheckGrossPdfExportTable(items, balance, t, helpers) {
  const {
    getOrderItemDescriptionCell,
    getItemDetails,
    getItemPricePerCt,
    isCustomByWeightItem,
    weightUnitShort,
    emptyDash,
  } = helpers;

  const needConversion = Boolean(
    balance?.showDualCurrency
    && balance?.defaultItemCurrency
    && balance?.checkCurrency
    && balance.defaultItemCurrency !== balance.checkCurrency,
  );

  const columns = [
    t('orderItems.columns.certNumber'),
    t('orderItems.columns.description'),
    t('orderItems.columns.weight'),
    t('cashiering.pricePerCt'),
    needConversion
      ? t('export.priceItem', { currency: getCurrencySymbol(balance?.defaultItemCurrency) })
      : t('cashiering.price'),
  ];
  if (needConversion) {
    columns.push(t('export.priceCheck', { currency: getCurrencySymbol(balance?.checkCurrency) }));
  }

  const sortedItems = sortOrderItemsByCreatedAt(items);
  let totalDefault = 0;
  let totalCheck = 0;

  const rows = sortedItems.map((item) => {
    const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
    const pricePerCt = getItemPricePerCt(item);
    const pdfPrices = formatGrossItemPriceForPdf(item, balance, emptyDash);
    if (needConversion) {
      const { defaultAmount, checkAmount } = resolveCheckLineAmount(item, balance);
      totalDefault += Number(defaultAmount) || 0;
      if (checkAmount != null) totalCheck += Number(checkAmount) || 0;
    } else {
      totalDefault += Number(item?.finalPrice) || 0;
    }
    const row = [
      getItemExportTypeLabel(item, t),
      getOrderItemDescriptionCell(item),
      getItemDetails(item),
      pricePerCt === emptyDash ? emptyDash : `${pricePerCt}/${weightSuffix}`,
      pdfPrices.itemPrice,
    ];
    if (needConversion) {
      row.push(pdfPrices.checkPrice ?? emptyDash);
    }
    return row;
  });

  const totalWeight = sortedItems.reduce((sum, item) => sum + (helpers.getItemCarat(item) || 0), 0);
  const currency = balance?.checkCurrency;
  const totalRow = [
    '',
    t('export.totalLabel'),
    t('export.totalWeightCt', { weight: totalWeight.toFixed(2) }),
    '',
    formatCurrencyForPdf(
      totalDefault,
      needConversion ? balance.defaultItemCurrency : (sortedItems[0]?.currency || currency || 'USD'),
    ),
  ];
  if (needConversion) {
    totalRow.push(formatCurrencyForPdf(totalCheck, currency));
  }

  return { columns, rows, totalRow, needConversion };
}

/** Whether the current staff user may call DELETE /api/checks/:id (empty check). */
export function canStaffDeleteEmptyCheck({ isInternal, userRole, customPermissions, hasPermissionFn, canCheckoutToCheckFn }) {
  if (!isInternal) return false;
  return hasPermissionFn(userRole, 'manage_orders', customPermissions)
    || hasPermissionFn(userRole, 'manage_checks', customPermissions)
    || canCheckoutToCheckFn({ isInternal, userRole, customPermissions });
}

/** True when check has no items, no active payments, and is not closed. */
export function isEmptyCheckDeletable({ items, transactions, checkStatus }) {
  if (checkStatus === CHECK_STATUSES.CLOSED) return false;
  if (items?.length > 0) return false;
  return !(transactions || []).some((tx) => tx.type === 'payment' && !tx.cancelled);
}
